import { Product, ProductSpec, ProductSummary } from 'types/product';
import { PagedResult } from 'types/api';

/** Shape típico de producto en Store API (camelCase). */
export type StoreProductApiItem = {
  id?: string;
  slug?: string;
  sku?: string;
  name?: string;
  Name?: string;
  description?: string;
  Description?: string;
  price?: number | null;
  currency?: string;
  imageUrl?: string;
  imageUrls?: string[];
  brandName?: string;
  categoryName?: string;
  categorySlug?: string;
  ramoName?: string;
  lineaName?: string;
  claseName?: string;
  atributo1?: string;
  atributo2?: string;
  atributo3?: string;
  barcode?: string;
  taxRate?: string;
  unitName?: string;
  stock?: number;
  availableQty?: number;
  inStock?: boolean;
  isOnPromotion?: boolean;
  isNew?: boolean;
  isPaused?: boolean;
  specs?: Array<{ key?: string; label?: string; value?: string; filterable?: boolean }>;
  codBarra?: string;
  codigoRamo?: string;
  codigoLinea?: string;
  codigoClase?: string;
};

const textOf = (item: StoreProductApiItem, ...keys: Array<keyof StoreProductApiItem>): string => {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const mapSpecs = (item: StoreProductApiItem): ProductSpec[] | undefined => {
  if (!item.specs?.length) return undefined;
  return item.specs
    .filter((s) => s.key && s.value)
    .map((s) => ({
      key: String(s.key),
      label: String(s.label || s.key),
      value: String(s.value),
      filterable: Boolean(s.filterable),
    }));
};

export const mapStoreProduct = (item: StoreProductApiItem): ProductSummary => {
  const sku = textOf(item, 'sku', 'id');
  const slug = textOf(item, 'slug') || sku;
  const apiName = textOf(item, 'name', 'Name');
  const apiDescription = textOf(item, 'description', 'Description');
  return {
    id: sku || slug,
    slug: slug || sku,
    name: apiName || apiDescription || sku || slug,
    description: apiDescription || undefined,
    sku: sku || undefined,
    price: item.price == null || Number.isNaN(Number(item.price)) ? 0 : Number(item.price),
    currency: item.currency,
    imageUrl: item.imageUrl || item.imageUrls?.[0],
    brandName: item.brandName,
    categoryName: item.categoryName,
    categorySlug: item.categorySlug,
    ramoName: item.ramoName,
    lineaName: item.lineaName,
    claseName: item.claseName,
    atributo1: item.atributo1,
    atributo2: item.atributo2,
    atributo3: item.atributo3,
    barcode: item.codBarra || item.barcode,
    taxRate: item.taxRate,
    unitName: item.unitName,
    isOnPromotion: item.isOnPromotion,
    isNew: item.isNew,
    isPaused: Boolean(item.isPaused),
    inStock: item.inStock,
    stock: item.stock ?? (item.availableQty != null ? Number(item.availableQty) : undefined),
    specs: mapSpecs(item),
  };
};

export const mapStoreProductDetail = (item: StoreProductApiItem): Product => ({
  ...mapStoreProduct(item),
  stock: item.stock ?? (item.availableQty != null ? Number(item.availableQty) : undefined),
  categoryName: item.categoryName,
  categorySlug: item.categorySlug,
  imageUrls: item.imageUrls,
  images: item.imageUrls,
  codigoRamo: item.codigoRamo,
  codigoLinea: item.codigoLinea,
  codigoClase: item.codigoClase,
});

export const asProductList = (data: unknown): StoreProductApiItem[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as PagedResult<StoreProductApiItem>).items)) {
    return (data as PagedResult<StoreProductApiItem>).items;
  }
  return [];
};
